import React from 'react';
import { Mail, Linkedin, Instagram, Send, Building2, GraduationCap, ArrowUpRight } from 'lucide-react';
import { useRevealOnScroll } from '../../hooks/useGsap';
import MagneticButton from '../ui/MagneticButton';

const Contact = () => {
  const ref = useRevealOnScroll();

  return (
    <section id="contact" ref={ref} className="relative py-28 bg-night-900 text-white overflow-hidden">
      {/* Background grid + glow */}
      <div className="absolute inset-0 bg-grid-green bg-[size:60px_60px] [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)] opacity-60 pointer-events-none" />
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[60rem] h-[60rem] rounded-full bg-primary-700/[0.15] blur-3xl pointer-events-none" />

      <div className="container-custom relative">
        <div className="max-w-3xl mb-16">
          <span className="eyebrow reveal">Entre em contato</span>
          <h2 className="section-title reveal mt-4">
            Vamos <span className="text-gradient">transformar</span> o agronegócio juntos
          </h2>
          <p className="section-subtitle reveal mt-6">
            Conte-nos sobre o seu desafio ou junte-se ao ecossistema.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Contact card */}
          <div className="reveal lg:col-span-2 bg-night-800 border border-white/[0.08] rounded-3xl p-8">
            <h3 className="font-display text-2xl font-bold mb-8 text-mist-50">Fale Conosco</h3>

            <a
              href="mailto:agrotech@inteli.edu.br"
              className="group block bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-primary-400 rounded-2xl p-5 transition-all duration-300"
            >
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-primary-400 text-night-950 flex items-center justify-center">
                  <Mail size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs uppercase tracking-wider text-mist-500">Email</p>
                  <p className="font-semibold text-mist-50 truncate">agrotech@inteli.edu.br</p>
                </div>
                <ArrowUpRight size={18} className="text-mist-500 group-hover:text-primary-400 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
              </div>
            </a>

            <div className="mt-8">
              <p className="text-xs uppercase tracking-[0.18em] text-mist-500 mb-4">Nossas redes sociais</p>
              <div className="grid grid-cols-2 gap-3">
                <a
                  href="https://www.linkedin.com/company/agrotech-inteli/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-3 bg-white/[0.04] hover:bg-[#0A66C2] hover:text-white border border-white/10 hover:border-[#0A66C2] px-4 py-3.5 rounded-xl transition-all duration-300"
                >
                  <Linkedin size={18}/>
                  <span className="font-medium text-sm">LinkedIn</span>
                  <ArrowUpRight size={14} className="ml-auto opacity-50 group-hover:opacity-100 transition-opacity" />
                </a>
                <a
                  href="https://www.instagram.com/agrotech_inteli/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative overflow-hidden flex items-center gap-3 bg-white/[0.04] border border-white/10 hover:border-pink-500 px-4 py-3.5 rounded-xl transition-all duration-300"
                >
                  <span
                    aria-hidden="true"
                    className="absolute inset-0 bg-gradient-to-r from-fuchsia-600 to-pink-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  />
                  <Instagram size={18} className="relative" />
                  <span className="relative font-medium text-sm">Instagram</span>
                  <ArrowUpRight size={14} className="relative ml-auto opacity-50 group-hover:opacity-100 transition-opacity" />
                </a>
              </div>
            </div>
          </div>

          {/* CTA card */}
          <div className="reveal lg:col-span-3 relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary-800 to-primary-950 border border-primary-400/20 p-8 md:p-10">
            <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-primary-400/25 blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-full h-1/2 bg-noise opacity-20 pointer-events-none" />

            <div className="relative">
              <h3 className="font-display text-3xl md:text-4xl font-bold mb-4">Quer Parceria?</h3>
              <p className="text-primary-50 mb-8 leading-relaxed max-w-xl">
                Se sua empresa enfrenta desafios no agronegócio e busca soluções
                tecnológicas inovadoras e de código aberto, vamos conversar!
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-night-950/30 backdrop-blur-sm rounded-2xl p-5 border border-white/10">
                  <div className="flex items-center gap-2 mb-3">
                    <Building2 size={16} />
                    <p className="font-semibold uppercase text-xs tracking-wider">Para Empresas</p>
                  </div>
                  <ul className="text-primary-50/90 text-sm space-y-1.5">
                    <li>• Soluções tecnológicas personalizadas</li>
                    <li>• Código aberto e transparente</li>
                    <li>• Equipe qualificada e dedicada</li>
                  </ul>
                </div>

                <div className="bg-night-950/30 backdrop-blur-sm rounded-2xl p-5 border border-white/10">
                  <div className="flex items-center gap-2 mb-3">
                    <GraduationCap size={16} />
                    <p className="font-semibold uppercase text-xs tracking-wider">Para Estudantes</p>
                  </div>
                  <ul className="text-primary-50/90 text-sm space-y-1.5">
                    <li>• Experiência prática em projetos reais</li>
                    <li>• Networking com o mercado</li>
                    <li>• Desenvolvimento de habilidades técnicas</li>
                  </ul>
                </div>
              </div>

              <MagneticButton
                href="mailto:agrotech@inteli.edu.br"
                strength={0.18}
                className="mt-8 inline-flex items-center gap-2 bg-primary-400 text-night-950 hover:bg-primary-300 font-semibold py-3.5 px-7 rounded-full transition-all duration-300 shadow-xl"
              >
                <Send size={18} />
                Enviar Email
              </MagneticButton>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
